import {
  GroupAPIResponse,
  GroupCreateAPI,
  GroupCreateAPIResponse,
  GroupWithComposableDevice,
} from './group.model';
import getGroupsListFromGroupsTree from './serialize/getGroupsListFromGroupsTree';
import { toBase64 } from '../../shared/util/base64';
import LocationAPI from '../locations/LocationAPI';
import Environment from '../../shared/constants/Environment';

const GroupAPI = LocationAPI.injectEndpoints({
  endpoints(build) {
    return {
      getGroupsList: build.query<GroupAPIResponse[], void>({
        query: () => ({
          url: `${Environment.backendAPIGroups}/`,
          method: 'get',
        }),
        transformResponse: (response: GroupAPIResponse[]) => {
          return getGroupsListFromGroupsTree(response);
        },
      }),
      getGroupById: build.query<GroupWithComposableDevice, string>({
        query: (id) => ({
          url: `${Environment.backendAPIGroups}/${toBase64(id)}`,
          method: 'get',
        }),
        transformResponse: (response: GroupWithComposableDevice) => {
          return response;
        },
        providesTags: ['locations-actions'],
      }),
      createGroup: build.mutation<GroupCreateAPIResponse, GroupCreateAPI>({
        query: (data) => ({
          url: Environment.backendAPIGroups,
          method: 'post',
          data,
        }),
        invalidatesTags: ['group-actions'],
      }),
      updateGroup: build.mutation<
        Partial<GroupWithComposableDevice>,
        { id: string; data: Partial<GroupWithComposableDevice> }
      >({
        query: ({ id, data }) => ({
          url: `${Environment.backendAPIGroups}/${toBase64(id)}`,
          method: 'put',
          data,
        }),
        invalidatesTags: ['group-actions'],
      }),
      deleteDeviceGroup: build.mutation<
        void,
        { groupId: string; commanderId: string }
      >({
        query: (args: { groupId: string; commanderId: string }) => {
          const { groupId, commanderId } = args;
          return {
            url: `${Environment.backendAPIGroups}/${toBase64(
              groupId
            )}/devices/${toBase64(commanderId)}`,
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          };
        },
        invalidatesTags: ['group-actions', 'device-actions'],
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          // Wait for mutation to succeed
          await queryFulfilled;
          // Pessimistic update the cache
          dispatch(
            GroupAPI.util.updateQueryData(
              'getGroupById',
              arg.groupId,
              (draft) => {
                if (draft.devices) {
                  draft.devices = draft.devices.filter(
                    (device) => device !== arg.commanderId
                  );
                }
              }
            )
          );
        },
      }),
    };
  },
});

export default GroupAPI;

export const {
  useGetGroupsListQuery,
  useGetGroupByIdQuery,
  useDeleteDeviceGroupMutation,
} = GroupAPI;
